import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../shared/lib/firebase';
import { useAuth } from '../auth/useAuth';
import type { NotificationDoc } from './types';

export function useNotifications(max = 30) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const [list, setList] = useState<NotificationDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      return;
    }
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', uid),
      orderBy('createdAt', 'desc'),
      limit(max),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setList(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<NotificationDoc, 'id'>),
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.error('[useNotifications]', err);
        setLoading(false);
      },
    );
    return unsub;
  }, [uid, max]);

  const unreadCount = useMemo(() => list.filter((n) => !n.read).length, [list]);
  const hasHighPriorityUnread = useMemo(
    () => list.some((n) => !n.read && n.priority === 'high'),
    [list],
  );

  async function markRead(id: string) {
    await updateDoc(doc(db, 'notifications', id), {
      read: true,
      readAt: serverTimestamp(),
    });
  }

  async function markAllRead() {
    const unread = list.filter((n) => !n.read);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    for (const n of unread) {
      batch.update(doc(db, 'notifications', n.id), {
        read: true,
        readAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  return { list, loading, unreadCount, hasHighPriorityUnread, markRead, markAllRead };
}
